/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Tue Nov 19 2024
 **/

import { useEffect, HTMLAttributes } from "react";

import useWhiteboardStore from "./stores/useWhiteboardStore";
import useWhiteboard from "./hooks/useWhiteboard";
import useMouseEvents from "./hooks/useMouseEvents";
import useDebugEvents from "./hooks/useDebugEvents";
import Drawing from "./drawing/Drawing";
import Toolbar from "./toolbar/Toolbar";
import Indicators from "./indicators/Indicators";
import ShrinkButton from "./windowState/ShrinkButton";
import MaximizeButton from "./windowState/MaximizeButton";
import { WhiteboardRootStyle } from "./emotion";

export default function Whiteboard({
	userId,
	zIndex,
	minimizeZIndex,
	...rest
}: { userId: string; zIndex: number; minimizeZIndex: number } & HTMLAttributes<HTMLDivElement>) {
	const whiteboardMode = useWhiteboardStore((state) => state.whiteboardMode);
	const isOpen = useWhiteboardStore((state) => state.isOpen);
	const windowState = useWhiteboardStore((state) => state.windowState);
	const setWhiteboard = useWhiteboardStore((state) => state.setWhiteboard);

	const whiteboard = useWhiteboard(userId);
	const mouseEvents = useMouseEvents(userId);
	const debugEvents = useDebugEvents();

	const targetZIndex = windowState === "minimize" ? minimizeZIndex : zIndex;

	// init whiteboard
	useEffect(() => {
		whiteboard.initialize();
		mouseEvents.initialize(whiteboard);
		debugEvents.initialize(whiteboard);
		setWhiteboard(whiteboard);
		return () => whiteboard.reset();
	}, []);

	return (
		<div
			id="whiteboard-root"
			css={WhiteboardRootStyle(targetZIndex + 1)}
			className={isOpen ? "" : "hide"}
			{...rest}
		>
			<Toolbar whiteboardMode={whiteboardMode} style={{ zIndex: targetZIndex + 2 }}>
				{whiteboardMode === "drawing" && <ShrinkButton />}
			</Toolbar>
			<Drawing whiteboardMode={whiteboardMode} style={{ zIndex: targetZIndex + 1 }}>
				{whiteboardMode === "drawing" && <MaximizeButton />}
			</Drawing>
			<Indicators style={{ zIndex: targetZIndex + 2 }} />
		</div>
	);
}
